// ==========================================================================
// screens/Vehicle.tsx — one vehicle: figures, trips to review, odometer,
// logbook, and the vehicle's details incl. the work-use switch (FF-008).
// ==========================================================================
import QueryUnavailable from "@/components/QueryUnavailable";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { BadgeDollarSign, Check, Pencil, Undo2 } from "lucide-react";
import Sheet, { Lbl, inputCls } from "@/components/Sheet";
import { SaleMoneyBadge } from "@/screens/Vehicles";
import { PageHeader, Section } from "@/components/Page";
import { Button, Card, ErrorText, Field } from "@/components/ui";
import { KmField } from "@/components/NumberPad";
import { Stat, weeklyKm } from "@/screens/Vehicles";
import MiniBars from "@/components/MiniBars";
import { tokenColour } from "@/lib/calendar";
import { useEntities } from "@/lib/household";
import { fromCents, money, toCents, useAccounts } from "@/lib/money";
import { useLoanPosition } from "@/lib/loans";
import type { Vehicle as VehicleRow } from "@/lib/vehicles";
import { SOLD_HOW, VEHICLE_TYPES, typeOf, useSellAsset, type Proceeds, type SoldHow, type VehicleType, bestLogbookWindow, fyOf, todayIso, useLogbooks, useOdometer, useReviewTrip, useSaveLogbook, useSaveOdometer, useSaveVehicle, useTaxRates, useTrips, useUsePeriods, useVehicles, vehicleStats, type Trip } from "@/lib/vehicles";

const d = (s: string) => format(new Date(s + "T00:00"), "d MMM");

function TripRow({ t, onReview }: { t: Trip; onReview: (use: "work" | "private" | null) => void }) {
  const use = t.reviewed_use ?? t.proposed_use;
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <span className="num w-12 shrink-0 text-sm text-ink-muted">{d(t.trip_date)}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{t.purpose}{t.to_place && <span className="font-normal text-ink-muted"> · {t.to_place}</span>}</span>
        <span className="num block text-xs text-ink-muted">{t.start_km.toLocaleString("en-AU")} → {t.end_km.toLocaleString("en-AU")}{t.job_ref && ` · ${t.job_ref}`}</span>
      </span>
      <span className="num shrink-0 text-lg font-semibold">{t.distance_km}<span className="text-xs font-normal text-ink-muted"> km</span></span>
      {t.reviewed_use ? (
        <button type="button" onClick={() => onReview(null)} title="Undo review" className={`shrink-0 rounded-sm px-2 py-0.5 text-xs font-semibold capitalize ${use === "work" ? "bg-primary/10 text-primary" : "bg-surface-raised text-ink-muted"}`}><Check className="mr-1 inline h-3 w-3" />{use}</button>
      ) : (
        <span className="flex shrink-0 gap-1">
          <button type="button" onClick={() => onReview("work")} className={`rounded-sm border px-2 py-0.5 text-xs ${t.proposed_use === "work" ? "border-primary text-primary" : "border-line text-ink-muted"}`}>Work</button>
          <button type="button" onClick={() => onReview("private")} className={`rounded-sm border px-2 py-0.5 text-xs ${t.proposed_use === "private" ? "border-primary text-primary" : "border-line text-ink-muted"}`}>Private</button>
        </span>
      )}
    </li>
  );
}

function DetailsForm({ vehicleId, onClose }: { vehicleId?: string; onClose: (id?: string) => void }) {
  const vehicles = useVehicles(); const periods = useUsePeriods(); const entities = useEntities();
  const save = useSaveVehicle();
  const v = vehicles.data?.find((x) => x.asset_id === vehicleId);
  const work = periods.data?.find((p) => p.asset_id === vehicleId && p.use === "work" && !p.valid_to);
  const [name, setName] = useState(v?.assets.name ?? "");
  const [vtype, setVtype] = useState<VehicleType>(v?.vehicle_type ?? "car");
  const [make, setMake] = useState(v?.make_model ?? "");
  const [rego, setRego] = useState(v?.rego ?? "");
  const [year, setYear] = useState(v?.year ? String(v.year) : "");
  const [acq, setAcq] = useState(v?.assets.acquired_on ?? "");
  const [owner, setOwner] = useState("");
  const [isWork, setIsWork] = useState(!!work);
  const [workFrom, setWorkFrom] = useState(work?.valid_from ?? "");
  const owners = entities.data?.filter((e) => !e.is_external) ?? [];
  // Value and finance (024)
  const accounts = useAccounts();
  const [cost, setCost] = useState(fromCents(v?.assets.cost_cents ?? null));
  const [finance, setFinance] = useState<VehicleRow["finance"]>(v?.finance ?? "owned");
  const [value, setValue] = useState(fromCents(v?.value_cents ?? null)); const [valueAt, setValueAt] = useState(v?.value_as_at ?? todayIso());
  const [loanId, setLoanId] = useState(v?.loan_account_id ?? "");
  const [provider, setProvider] = useState(v?.lease_provider ?? ""); const [payment, setPayment] = useState(fromCents(v?.lease_payment_cents ?? null));
  const [freq, setFreq] = useState<NonNullable<VehicleRow["lease_frequency"]>>(v?.lease_frequency ?? "fortnightly");
  const [ends, setEnds] = useState(v?.lease_ends_on ?? ""); const [residual, setResidual] = useState(fromCents(v?.residual_cents ?? null));
  const loans = (accounts.data ?? []).filter((a) => a.kind === "loan" && !a.closed_on);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const lease = finance === "novated";
    const id = await save.mutateAsync({ asset_id: vehicleId, name: name.trim(), vehicle_type: vtype, make_model: make.trim() || null, rego: rego.trim().toUpperCase() || null, year: year ? Number(year) : null, acquired_on: acq || null, owner_entity_id: vehicleId ? undefined : owner || undefined, work_from: isWork ? workFrom || todayIso() : null,
      cost_cents: toCents(cost),
      money: { finance, value_cents: lease ? null : toCents(value), value_as_at: lease || !value ? null : valueAt || todayIso(), loan_account_id: finance === "loan" ? loanId || null : null,
        lease_provider: lease ? provider.trim() || null : null, lease_payment_cents: lease ? toCents(payment) : null, lease_frequency: lease ? freq : null, lease_ends_on: lease ? ends || null : null, residual_cents: lease ? toCents(residual) : null } });
    onClose(id);
  }
  return (
    <Card className="space-y-4 border-primary/40">
      <h2 className="text-lg font-semibold">{vehicleId ? "Vehicle details" : "Add a vehicle"}</h2>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <span className="text-sm font-medium text-ink-muted">What is it</span>
          <div className="mt-1.5 grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-9">
            {VEHICLE_TYPES.map((t) => (
              <button key={t.key} type="button" onClick={() => setVtype(t.key)} className={`flex flex-col items-center gap-1 rounded-xl py-2.5 text-xs ${vtype === t.key ? "bg-primary font-semibold text-white" : "bg-surface-raised"}`}>
                <span className="text-2xl">{t.emoji}</span>{t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid gap-3 lg:grid-cols-2">
          <Field label="Name" required value={name} onChange={(e) => setName(e.target.value)} placeholder={vtype === "boat" ? "The tinny" : vtype === "trailer" ? "Box trailer" : vtype === "motorbike" ? "The bike" : "Prado"} />
          <Field label="Make and model" value={make} onChange={(e) => setMake(e.target.value)} placeholder="Toyota Prado" />
          <Field label="Rego" value={rego} onChange={(e) => setRego(e.target.value)} />
          <Field label="Year" inputMode="numeric" pattern="[0-9]*" value={year} onChange={(e) => setYear(e.target.value.replace(/\D/g, "").slice(0, 4))} />
          <Field label="Bought on" type="date" value={acq} onChange={(e) => setAcq(e.target.value)} />
          {!vehicleId && (
            <label className="block space-y-1">
              <span className="text-sm font-medium text-ink-muted">Owner</span>
              <select required value={owner} onChange={(e) => setOwner(e.target.value)} className="w-full rounded-md border border-line bg-bg px-3 py-3 text-ink">
                <option value="">Choose…</option>
                {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
          )}
        </div>
        <div className="space-y-3 rounded-md bg-surface-raised p-3">
          <span className="font-medium">How it's paid for</span>
          <div className="flex flex-wrap gap-2">
            {([["owned", "Owned outright"], ["loan", "On a loan"], ["novated", "Novated lease (salary sacrifice)"]] as const).filter(([k]) => k !== "novated" || typeOf(vtype).trips).map(([k, l]) => (
              <button key={k} type="button" onClick={() => setFinance(k)} className={`rounded-full px-3.5 py-1.5 text-sm ${finance === k ? "bg-primary font-semibold text-white" : "bg-surface ring-1 ring-line"}`}>{l}</button>
            ))}
          </div>
          <div className="grid gap-3 lg:grid-cols-2">
            <Field label="Paid ($, optional)" inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} />
            {finance !== "novated" && <Field label="Worth now ($, estimate)" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />}
            {finance !== "novated" && value && <Field label="Estimate as at" type="date" value={valueAt} onChange={(e) => setValueAt(e.target.value)} />}
            {finance === "loan" && (
              <label className="block space-y-1">
                <span className="text-sm font-medium text-ink-muted">The loan</span>
                <select value={loanId} onChange={(e) => setLoanId(e.target.value)} className="w-full rounded-md border border-line bg-bg px-3 py-3 text-ink">
                  <option value="">{loans.length ? "Choose the loan account…" : "Add the loan under Money → Settings → Accounts first"}</option>
                  {loans.map((a) => <option key={a.id} value={a.id}>{a.name}{a.institution ? ` (${a.institution})` : ""}</option>)}
                </select>
              </label>
            )}
            {finance === "novated" && (
              <>
                <Field label="Lease company" value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="e.g. the novated lease provider" />
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Each pay ($)" inputMode="decimal" value={payment} onChange={(e) => setPayment(e.target.value)} />
                  <label className="block space-y-1"><span className="text-sm font-medium text-ink-muted">How often</span>
                    <select value={freq} onChange={(e) => setFreq(e.target.value as typeof freq)} className="w-full rounded-md border border-line bg-bg px-3 py-3 text-ink"><option value="weekly">Weekly</option><option value="fortnightly">Fortnightly</option><option value="monthly">Monthly</option></select>
                  </label>
                </div>
                <Field label="Lease ends" type="date" value={ends} onChange={(e) => setEnds(e.target.value)} />
                <Field label="Residual at the end ($)" inputMode="decimal" value={residual} onChange={(e) => setResidual(e.target.value)} />
              </>
            )}
          </div>
          <p className="text-xs text-ink-muted">{finance === "novated" ? "With a novated lease the lease company owns the car until the residual is paid, so it isn't counted in net worth. The payments come out of the pay before tax (and some after); the payslip shows the split." : finance === "loan" ? "The car counts at its estimate in net worth, and the loan's balance comes off." : "The car counts at its estimate in net worth."}</p>
        </div>
        <div className="rounded-md bg-surface-raised p-3 space-y-3">
          <label className="flex items-center justify-between">
            <span><span className="font-medium">Used for work</span><span className="block text-xs text-ink-muted">On: trips, logbook and depreciation apply. Off: personal only; costs still tracked.</span></span>
            <input type="checkbox" checked={isWork} onChange={(e) => setIsWork(e.target.checked)} className="h-6 w-6 accent-primary" />
          </label>
          {isWork && <Field label="Work use from" type="date" value={workFrom} onChange={(e) => setWorkFrom(e.target.value)} />}
        </div>
        <ErrorText>{save.error ? "Couldn't save. Try again." : null}</ErrorText>
        <div className="flex gap-3">
          <Button type="submit" disabled={save.isPending} className="lg:!w-auto lg:px-6">{save.isPending ? "Saving…" : "Save"}</Button>
          <Button type="button" variant="ghost" onClick={() => onClose()} className="lg:!w-auto">Cancel</Button>
        </div>
      </form>
    </Card>
  );
}

export function NewVehicle() {
  const nav = useNavigate();
  return (<><PageHeader title="Add a vehicle" /><DetailsForm onClose={(id) => nav(id ? `/vehicles/${id}` : "/vehicles")} /></>);
}

export default function Vehicle() {
  const { id = "" } = useParams();
  const vehicles = useVehicles(); const trips = useTrips(id); const periods = useUsePeriods(); const odo = useOdometer(id); const logbooks = useLogbooks(id); const rates = useTaxRates();
  const review = useReviewTrip(); const saveOdo = useSaveOdometer(); const saveLb = useSaveLogbook();
  const [editing, setEditing] = useState(false);
  const [odoKm, setOdoKm] = useState(""); const [odoKind, setOdoKind] = useState("other"); const [odoDate, setOdoDate] = useState(todayIso());
  const [showAll, setShowAll] = useState(false);
  const v = vehicles.data?.find((x) => x.asset_id === id);
  const fy = fyOf(new Date());
  const s = vehicleStats(id, trips.data ?? [], periods.data ?? [], fy);
  const rate = rates.data?.find((r) => r.financial_year_id === fy && r.key === "car_cents_per_km")?.value ?? null;
  const running = logbooks.data?.find((l) => l.status === "running");
  const best = bestLogbookWindow(trips.data ?? []);
  const locked = logbooks.data?.find((l) => l.status !== "running");
  const lastKnown = Math.max(s.lastKm ?? 0, ...(odo.data ?? []).map((o) => o.km));
  useEffect(() => { if (odoKm === "" && lastKnown > 0) setOdoKm(String(lastKnown)); }, [lastKnown]); // eslint-disable-line
  const toReview = (trips.data ?? []).filter((t) => !t.reviewed_use);
  const list = showAll ? trips.data ?? [] : (trips.data ?? []).slice(0, 10);

  const [selling, setSelling] = useState(false);
  if (vehicles.isError) return <QueryUnavailable name="Vehicle" />;
  if (!v) return <PageHeader title="Vehicle" subtitle={vehicles.isLoading ? "Loading…" : "Not found."} />;
  const kind = typeOf(v.vehicle_type);
  const sold = !!v.assets.disposed_on;

  return (
    <>
      <PageHeader title={<><span className="mr-3 font-body">{kind.emoji}</span>{v.assets.name}</>} subtitle={[kind.label, v.year, v.make_model, v.rego].filter(Boolean).join(" · ") || "Details to fill in"}
        action={sold ? undefined : (
          <div className="flex gap-2">
            {kind.trips && <Link to={`/vehicles/${id}/trip`} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-ink">Log a trip</Link>}
            <button type="button" onClick={() => setSelling(true)} className="flex items-center gap-1.5 rounded-md bg-surface px-4 py-2 text-sm font-semibold ring-1 ring-line hover:ring-primary/40"><BadgeDollarSign className="h-4 w-4" />Mark as sold</button>
          </div>
        )} />
      {sold && <SoldBanner v={v} onEdit={() => setSelling(true)} />}
      {selling && <SellSheet v={v} onClose={() => setSelling(false)} />}

      {editing && <div className="mb-6"><DetailsForm vehicleId={id} onClose={() => setEditing(false)} /></div>}

      <div className="grid gap-8 lg:grid-cols-3">
        <div className="min-w-0 space-y-8 lg:col-span-2">
          {(!kind.trips || sold) && <ToyPanel v={v} />}
          {kind.trips && !sold && <>
          <div className="overflow-hidden rounded-lg bg-surface shadow-soft ring-1 ring-line/70">
            <div className="grid grid-cols-2 gap-5 bg-panel px-6 py-5 text-panel-ink sm:grid-cols-4">
              <div><div className="num text-[34px] font-semibold leading-none text-[#F293A9]">{s.workKm.toLocaleString("en-AU")}</div><div className="mt-1 text-xs text-panel-muted">work km · {fy}</div></div>
              <div><div className="num text-2xl font-semibold">{s.privateKm.toLocaleString("en-AU")}</div><div className="text-xs text-panel-muted">private km</div></div>
              <div><div className="num text-2xl font-semibold">{s.trips}</div><div className="text-xs text-panel-muted">trips</div></div>
              <div><div className="num text-2xl font-semibold">{rate === null ? "—" : `$${Math.round((Math.min(s.workKm, 5000) * rate) / 100).toLocaleString("en-AU")}`}</div><div className="text-xs text-panel-muted">cents-per-km estimate</div></div>
            </div>
            <div className="p-6">
              <MiniBars data={weeklyKm(trips.data ?? [])} colour={tokenColour("chart-1")} colour2={tokenColour("chart-1", 0.28)} unit="km" height={96} />
              <p className="mt-2 text-xs text-ink-muted">Last 12 weeks · darker is work, lighter is private</p>
              {rate === null && <p className="mt-2 text-xs text-warn">The {fy} rate hasn't been checked against the ATO yet, so no estimate is shown.</p>}
            </div>
          </div>

          {toReview.length > 0 && (
            <Section title={`To review · ${toReview.length}`}>
              <p className="mb-2 text-sm text-ink-muted">Confirm work or private for each trip. Home to depot counts as private.</p>
              <Card className="p-0"><ul className="divide-y divide-line">{toReview.map((t) => <TripRow key={t.id} t={t} onReview={(use) => review.mutate({ id: t.id, use })} />)}</ul></Card>
            </Section>
          )}

          <Section title="Trips">
            {list.length === 0 ? <Card className="text-ink-muted">No trips yet.</Card> : (
              <Card className="p-0">
                <ul className="divide-y divide-line">{list.map((t) => <TripRow key={t.id} t={t} onReview={(use) => review.mutate({ id: t.id, use })} />)}</ul>
                {(trips.data?.length ?? 0) > 10 && <button type="button" onClick={() => setShowAll((x) => !x)} className="block w-full border-t border-line px-4 py-3 text-left text-sm font-medium text-primary">{showAll ? "Show fewer" : `Show all ${trips.data?.length}`}</button>}
              </Card>
            )}
          </Section>
          </>}
        </div>

        <div className="space-y-8">
          <MoneyCard v={v} onEdit={() => { setEditing(true); window.scrollTo({ top: 0 }); }} />
          {kind.trips && !sold && <>
          <Section title="Logbook">
            <Card className="space-y-3">
              <p className="text-sm text-ink-muted">Log every trip, work and private. The ATO's logbook method needs one 12-week stretch; the app picks the best one from everything you've logged. That work share then applies to real costs (fuel, rego, insurance, servicing, loan interest, depreciation) for five years.</p>
              {locked ? (
                <div className="rounded-md bg-surface-raised p-3">
                  <p className="font-medium">Locked in <span className="num text-ink-muted">{d(locked.starts_on)} – {d(locked.ends_on)}</span></p>
                  <p className="text-xs text-ink-muted">{locked.status}</p>
                </div>
              ) : best ? (
                <div className="rounded-md bg-surface-raised p-3 space-y-2">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm">{best.enoughData ? "Best 12 weeks" : "Work share so far"}</span>
                    <span className="num text-2xl font-semibold text-primary">{Math.round(best.pct)}%<span className="text-xs font-normal text-ink-muted"> work</span></span>
                  </div>
                  <p className="num text-xs text-ink-muted">{d(best.from)} – {d(best.to)} · {best.workKm.toLocaleString("en-AU")} of {best.totalKm.toLocaleString("en-AU")} km · {best.trips} trips</p>
                  {s.totalKm > 0 && <p className="text-sm">Average over everything logged this year: <span className="num font-semibold">{Math.round((s.workKm / s.totalKm) * 100)}%</span> work</p>}
                  <p className="text-xs text-ink-muted">The ATO expects the 12 weeks to be typical of how the car is used, so if the best period is well above the average, use one closer to it.</p>
                  {best.enoughData ? (
                    <Button disabled={saveLb.isPending} onClick={() => saveLb.mutate({ vehicle_id: id, starts_on: best.from, ends_on: best.to, opening_km: null, status: "complete" })}>Lock this period in</Button>
                  ) : (
                    <p className="text-xs text-ink-muted">Keep logging: 12 weeks of trips are needed before a period can be locked in.</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-ink-muted">No reviewed trips yet.</p>
              )}
              {running && <p className="num text-xs text-ink-muted">Old running period {d(running.starts_on)} – {d(running.ends_on)}</p>}
            </Card>
          </Section>

          <Section title="Odometer">
            <Card className="space-y-3">
              {odo.data?.slice(0, 3).map((o) => <p key={o.id} className="flex justify-between text-sm"><span className="text-ink-muted">{d(o.read_on)} · {o.kind.replace("_", " ")}</span><span className="num font-medium">{o.km.toLocaleString("en-AU")} km</span></p>)}
              <form onSubmit={(e) => { e.preventDefault(); if (!odoKm) return; saveOdo.mutate({ vehicle_id: id, read_on: odoDate, km: Number(odoKm), kind: odoKind }); }} className="space-y-2 border-t border-line pt-3">
                <KmField label="Add a reading" value={odoKm} onChange={setOdoKm} hint={lastKnown > 0 ? "Starts from the last known reading — change it to match the dash" : undefined} />
                <div className="grid grid-cols-2 gap-2">
                  <input type="date" value={odoDate} max={todayIso()} onChange={(e) => setOdoDate(e.target.value)} className="rounded-md border border-line bg-bg px-2 py-2 text-sm text-ink" />
                  <select value={odoKind} onChange={(e) => setOdoKind(e.target.value)} className="rounded-md border border-line bg-bg px-2 py-2 text-sm text-ink">
                    <option value="other">Reading</option><option value="year_open">1 July (year open)</option><option value="year_close">30 June (year close)</option><option value="service">Service</option>
                  </select>
                </div>
                <Button type="submit" variant="ghost" disabled={!odoKm || saveOdo.isPending} className="!text-primary">Save reading</Button>
              </form>
            </Card>
          </Section>
          </>}

          <Section title="Costs">
            <Card className="text-sm text-ink-muted">{kind.trips ? "Fuel, rego, insurance, servicing and any loan" : "Rego, insurance, storage, servicing and any loan"} show here once they're coded to this {kind.label.toLowerCase()} in Reconcile. Each cost is recorded once and pointed at it.</Card>
          </Section>

          <Section title="Details">
            <Card className="space-y-2 text-sm">
              <p className="flex justify-between"><span className="text-ink-muted">Work use</span><span>{s.workFrom ? `from ${d(s.workFrom)}` : "No"}</span></p>
              <p className="flex justify-between"><span className="text-ink-muted">Bought</span><span>{v.assets.acquired_on ? d(v.assets.acquired_on) : "—"}</span></p>
              {v.assets.notes && <p className="text-ink-muted">{v.assets.notes}</p>}
              <button type="button" onClick={() => { setEditing(true); window.scrollTo({ top: 0 }); }} className="flex items-center gap-1 pt-1 font-medium text-primary"><Pencil className="h-4 w-4" /> Edit details</button>
            </Card>
          </Section>
        </div>
      </div>
    </>
  );
}

/** What the vehicle is worth and how it's paid for (024). */
function MoneyCard({ v, onEdit }: { v: VehicleRow; onEdit: () => void }) {
  const pos = useLoanPosition(v.finance === "loan" ? v.loan_account_id : null);
  const perYear = v.lease_payment_cents ? v.lease_payment_cents * (v.lease_frequency === "weekly" ? 52 : v.lease_frequency === "monthly" ? 12 : 26) : null;
  const equity = v.value_cents !== null ? v.value_cents - (pos?.owing ?? 0) : null;
  return (
    <Section title="Value and finance">
      <Card className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">{v.finance === "owned" ? "Owned outright" : v.finance === "loan" ? "Car loan" : "Novated lease"}</span>
          <button type="button" onClick={onEdit} className="flex items-center gap-1 text-sm font-medium text-primary"><Pencil className="h-4 w-4" /> Edit</button>
        </div>
        {v.finance === "novated" ? (
          <div className="grid grid-cols-2 gap-4">
            <div><div className="num text-2xl font-semibold">{v.lease_payment_cents ? money(v.lease_payment_cents) : "—"}</div><div className="text-xs text-ink-muted">{v.lease_frequency ?? "each pay"}{v.lease_provider ? ` · ${v.lease_provider}` : ""}</div></div>
            <div><div className="num text-2xl font-semibold">{perYear ? money(perYear) : "—"}</div><div className="text-xs text-ink-muted">a year</div></div>
            <div><div className="num text-lg font-semibold">{v.lease_ends_on ? format(new Date(v.lease_ends_on + "T00:00"), "MMM yyyy") : "—"}</div><div className="text-xs text-ink-muted">lease ends</div></div>
            <div><div className="num text-lg font-semibold">{v.residual_cents ? money(v.residual_cents) : "—"}</div><div className="text-xs text-ink-muted">residual to pay then</div></div>
            <p className="col-span-2 text-xs text-ink-muted">The lease company owns it until the residual is paid, so it's not in net worth. Code the payments when they show on the payslip or bank to a car code so the cost is tracked.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div><div className="num text-2xl font-semibold">{v.value_cents !== null ? money(v.value_cents) : "—"}</div><div className="text-xs text-ink-muted">{v.value_as_at ? `worth, as at ${format(new Date(v.value_as_at + "T00:00"), "MMM yyyy")}` : "add a value estimate"}</div></div>
            <div><div className="num text-2xl font-semibold">{v.assets.cost_cents !== null ? money(v.assets.cost_cents) : "—"}</div><div className="text-xs text-ink-muted">paid{v.assets.acquired_on ? ` · ${format(new Date(v.assets.acquired_on + "T00:00"), "MMM yyyy")}` : ""}</div></div>
            {v.finance === "loan" && <>
              <div><div className="num text-lg font-semibold">{pos?.owing !== null && pos?.owing !== undefined ? money(pos.owing) : "—"}</div><div className="text-xs text-ink-muted">loan owing{pos?.loan ? ` · ${pos.loan.name}` : ""}</div></div>
              <div><div className={`num text-lg font-semibold ${equity !== null && equity < 0 ? "text-bad" : "text-good"}`}>{equity !== null ? money(equity) : "—"}</div><div className="text-xs text-ink-muted">equity in it</div></div>
            </>}
          </div>
        )}
      </Card>
    </Section>
  );
}

/** For things without trips (boat, trailer, jet ski, caravan): the money at a glance. */
function ToyPanel({ v }: { v: VehicleRow }) {
  const k = typeOf(v.vehicle_type);
  return (
    <div className="overflow-hidden rounded-lg bg-surface shadow-soft ring-1 ring-line/70">
      <div className="flex items-center gap-5 bg-panel px-6 py-6 text-panel-ink">
        <span className="text-5xl">{k.emoji}</span>
        <div>
          <div className="num text-[34px] font-semibold leading-none text-[#F293A9]">{v.value_cents !== null ? money(v.value_cents) : "—"}</div>
          <div className="mt-1 text-xs text-panel-muted">{v.value_as_at ? `worth, as at ${format(new Date(v.value_as_at + "T00:00"), "MMM yyyy")}` : "add a value estimate in Edit"}</div>
        </div>
      </div>
      <p className="p-6 text-sm text-ink-muted">{v.assets.disposed_on ? "Sold, so it's no longer counted in net worth. Its trips and costs stay in the history." : `No trips or logbook for a ${k.label.toLowerCase()}. Keep its rego and value up to date here, and code its costs to it in Reconcile so they add up on this page.`}</p>
    </div>
  );
}

/** Sold: what for, how, and where the money is up to (032). */
function SoldBanner({ v, onEdit }: { v: VehicleRow; onEdit: () => void }) {
  const a = v.assets; const accounts = useAccounts(); const vehicles = useVehicles();
  const acc = accounts.data?.find((x) => x.id === a.proceeds_account_id);
  const forWhat = vehicles.data?.find((x) => x.asset_id === a.traded_for_asset_id);
  return (
    <div className="mb-8 flex flex-wrap items-center gap-4 rounded-2xl bg-surface p-5 shadow-soft ring-1 ring-line/70">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-good/15 text-good"><BadgeDollarSign className="h-6 w-6" /></span>
      <div className="min-w-0 flex-1">
        <div className="font-semibold">Sold {format(new Date(a.disposed_on! + "T00:00"), "d MMMM yyyy")}{a.sold_price_cents !== null ? ` for ${money(a.sold_price_cents)}` : ""}</div>
        <div className="text-sm text-ink-muted">
          {[SOLD_HOW.find((h) => h.key === a.sold_how)?.label, a.sold_to ? `to ${a.sold_to}` : null,
            a.proceeds === "bank" ? `money into ${acc?.name ?? "the bank"}` : a.proceeds === "trade_in" ? `traded in${forWhat ? ` on the ${forWhat.assets.name}` : ""}` : a.proceeds === "cash" ? "paid in cash" : null,
            a.cost_cents !== null && a.sold_price_cents !== null ? `${a.sold_price_cents >= a.cost_cents ? "up" : "down"} ${money(Math.abs(a.sold_price_cents - a.cost_cents))} on what it cost` : null].filter(Boolean).join(", ")}
        </div>
        {a.proceeds === "bank" && !a.proceeds_transaction_id && <div className="mt-1 text-xs text-ink-muted">When the bank CSV with the deposit is imported, Reconcile will suggest matching it to this sale.</div>}
      </div>
      <SaleMoneyBadge v={v} />
      <button type="button" onClick={onEdit} className="flex items-center gap-1 text-sm font-medium text-primary"><Pencil className="h-4 w-4" />Edit sale</button>
    </div>
  );
}

function SellSheet({ v, onClose }: { v: VehicleRow; onClose: () => void }) {
  const sell = useSellAsset(); const accounts = useAccounts(); const vehicles = useVehicles();
  const a = v.assets;
  const [on, setOn] = useState(a.disposed_on ?? todayIso());
  const [price, setPrice] = useState(fromCents(a.sold_price_cents ?? null));
  const [how, setHow] = useState<SoldHow>(a.sold_how ?? "private");
  const [to, setTo] = useState(a.sold_to ?? "");
  const [proceeds, setProceeds] = useState<Proceeds>(a.proceeds ?? "bank");
  const [acc, setAcc] = useState(a.proceeds_account_id ?? "");
  const [expect, setExpect] = useState(a.proceeds_expected_on ?? "");
  const [traded, setTraded] = useState(a.traded_for_asset_id ?? "");
  const [note, setNote] = useState(a.sale_note ?? "");
  const [err, setErr] = useState<string | null>(null); const [undo, setUndo] = useState(false);
  const banks = (accounts.data ?? []).filter((x) => !x.closed_on && ["everyday", "savings", "offset", "cash", "other"].includes(x.kind));
  const others = (vehicles.data ?? []).filter((x) => x.asset_id !== v.asset_id);
  const submit = () => {
    if (proceeds === "bank" && !acc) { setErr("Which account is the money going into?"); return; }
    const cents = price.trim() ? toCents(price) : null;
    if (["private", "dealer", "auction", "marketplace"].includes(how) && !cents) { setErr("What did it sell for?"); return; }
    sell.mutate({ assetId: v.asset_id, disposed_on: on, sold_price_cents: cents, sold_how: how, sold_to: to.trim() || null, proceeds,
      proceeds_account_id: proceeds === "bank" ? acc : null, proceeds_expected_on: proceeds === "bank" ? expect || on : null,
      proceeds_transaction_id: proceeds === "bank" && acc === a.proceeds_account_id ? a.proceeds_transaction_id : null,
      traded_for_asset_id: proceeds === "trade_in" ? traded || null : null, sale_note: note.trim() || null }, { onSuccess: onClose, onError: (e) => setErr(e.message) });
  };
  return (
    <Sheet title={a.disposed_on ? `Sale: ${a.name}` : `Sell ${a.name}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Lbl label="Sold on"><input type="date" className={inputCls} value={on} onChange={(e) => setOn(e.target.value)} /></Lbl>
          <Lbl label="For ($)"><input className={inputCls} inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="18,500" /></Lbl>
        </div>
        <div>
          <span className="text-sm font-medium text-ink-muted">How</span>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {SOLD_HOW.map((h) => <button key={h.key} type="button" onClick={() => { setHow(h.key); if (h.key === "trade_in") setProceeds("trade_in"); if (h.key === "written_off" || h.key === "gifted") setProceeds("none"); }} className={`rounded-full px-3 py-1.5 text-sm ${how === h.key ? "bg-primary font-semibold text-white" : "bg-surface-raised"}`}>{h.label}</button>)}
          </div>
        </div>
        <Lbl label="Sold to (optional)"><input className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} placeholder="A name, or the dealer" /></Lbl>
        <div>
          <span className="text-sm font-medium text-ink-muted">Where the money went</span>
          <div className="mt-1.5 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {([["bank", "Into the bank"], ["cash", "Cash"], ["trade_in", "Traded in"], ["none", "No money"]] as const).map(([k, l]) => (
              <button key={k} type="button" onClick={() => setProceeds(k)} className={`rounded-xl py-2.5 text-sm ${proceeds === k ? "bg-primary font-semibold text-white" : "bg-surface-raised"}`}>{l}</button>
            ))}
          </div>
        </div>
        {proceeds === "bank" && (
          <div className="grid gap-3 rounded-xl bg-surface-raised p-3 sm:grid-cols-2">
            <Lbl label="Into which account">
              <select className={inputCls} value={acc} onChange={(e) => setAcc(e.target.value)}>
                <option value="">Choose…</option>
                {banks.map((x) => <option key={x.id} value={x.id}>{x.name}{x.institution ? ` (${x.institution})` : ""}</option>)}
              </select>
            </Lbl>
            <Lbl label="Expected by"><input type="date" className={inputCls} value={expect || on} onChange={(e) => setExpect(e.target.value)} /></Lbl>
            <p className="text-xs text-ink-muted sm:col-span-2">It waits as expected money. When the deposit for the same amount shows up in that account's CSV (within three weeks), Reconcile suggests it as this sale, coded 940: a money movement, not income.</p>
          </div>
        )}
        {proceeds === "trade_in" && (
          <Lbl label="Traded in on" hint="Add the new one first if it isn't here yet">
            <select className={inputCls} value={traded} onChange={(e) => setTraded(e.target.value)}>
              <option value="">Choose…</option>
              {others.map((x) => <option key={x.asset_id} value={x.asset_id}>{x.assets.name}</option>)}
            </select>
          </Lbl>
        )}
        <Lbl label="Note (optional)"><input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} /></Lbl>
        <ErrorText>{err}</ErrorText>
        <div className="flex gap-2">
          <button type="button" onClick={submit} disabled={sell.isPending} className="flex-1 rounded-full bg-primary py-3 font-semibold text-primary-ink disabled:opacity-50">{a.disposed_on ? "Save" : "Mark as sold"}</button>
          {a.disposed_on && (undo
            ? <button type="button" onClick={() => sell.mutate({ assetId: v.asset_id, disposed_on: null }, { onSuccess: onClose })} className="rounded-full bg-bad/10 px-4 py-3 text-sm font-semibold text-bad">Yes, not sold</button>
            : <button type="button" onClick={() => setUndo(true)} className="flex items-center gap-1.5 rounded-full bg-surface-raised px-4 py-3 text-sm font-semibold"><Undo2 className="h-4 w-4" />Undo sale</button>)}
        </div>
      </div>
    </Sheet>
  );
}
